/**
 * Range filter utilities for dashboard filtering
 * Extracted from app._index.tsx to eliminate duplication
 */

/**
 * Creates a validated handler for range filter inputs.
 * Only allows empty strings or positive integers.
 *
 * @param setter - State setter function for the range value
 * @returns Handler function compatible with Polaris TextField onChange
 */
export const createRangeHandler = (setter: (value: string) => void) =>
  (value: string): void => {
    if (value === '' || /^\d+$/.test(value)) {
      setter(value);
    }
  };

/**
 * Validates that min is not greater than max.
 * Returns true if there is an error (min > max).
 *
 * @param min - Minimum value as string
 * @param max - Maximum value as string
 * @returns true if min > max (error condition), false otherwise
 */
export const validateRange = (min: string, max: string): boolean =>
  !!(min && max && parseInt(min, 10) > parseInt(max, 10));

/**
 * Checks if a numeric value falls within a range.
 * Handles cases where min or max are not provided.
 *
 * @param value - The numeric value to check
 * @param min - Minimum bound as string (empty string = no minimum)
 * @param max - Maximum bound as string (empty string = no maximum)
 * @returns true if value is within range, false otherwise
 */
export const matchesRange = (value: number, min: string, max: string): boolean => {
  const minVal = min ? parseInt(min, 10) : null;
  const maxVal = max ? parseInt(max, 10) : null;

  if (minVal !== null && maxVal !== null) {
    return value >= minVal && value <= maxVal;
  }
  if (minVal !== null) {
    return value >= minVal;
  }
  if (maxVal !== null) {
    return value <= maxVal;
  }
  return true;
};

/**
 * Interface for range filter state
 */
export interface RangeFilterState {
  min: string;
  max: string;
}

/**
 * Interface for combined range filter states used in dashboard
 */
export interface DashboardRangeFilters {
  days: RangeFilterState;
  inventory: RangeFilterState;
}
